import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { drawCard, drawSceneBackground, drawSceneTitle, fillRoundedRect } from "../../utils/draw";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import type { QuestView } from "../systems/Quests";
import { MenuScene } from "./MenuScene";
import type { GameContext, Scene } from "./Scene";

const CARD_X = 20;
const CARD_W = 320;
const CARD_H = 92;
const CARD_GAP = 12;
const LIST_TOP = 128;

/** The claim button rect for the card drawn at `cardY` (shared by draw + hit-test). */
function claimRect(cardY: number): { x: number; y: number; w: number; h: number } {
  return { x: CARD_X + CARD_W - 90, y: cardY + 44, w: 76, h: 30 };
}

export class QuestsScene implements Scene {
  private pollAccum = 0;
  private polling = false;
  private claiming = false;
  private entered = false;
  private toast = "";
  private toastT = 0;

  constructor(private readonly ctxMgr: GameContext) {}

  private get myName(): string {
    return this.ctxMgr.settings.playerName;
  }
  private get token(): string {
    return this.ctxMgr.settings.authToken;
  }

  enter(): void {
    this.entered = true;
    void this.reload();
    this.rebuild();
  }

  exit(): void {
    this.entered = false;
    this.ctxMgr.buttons.clear();
  }

  update(dt: number): void {
    if (this.toastT > 0) this.toastT -= dt;
    // Re-poll a few seconds apart so progress from a run finished in another tab
    // (or a midnight rollover) shows up while the screen is open.
    this.pollAccum += dt;
    if (this.pollAccum >= 5 && !this.polling && !this.claiming) {
      this.pollAccum = 0;
      void this.reload();
    }
  }

  private async reload(): Promise<void> {
    if (!this.ctxMgr.settings.isLoggedIn()) return;
    this.polling = true;
    await this.ctxMgr.quests.refresh(this.myName, this.token);
    this.polling = false;
    if (this.entered) this.rebuild();
  }

  private async doClaim(q: QuestView): Promise<void> {
    if (this.claiming) return;
    this.claiming = true;
    const res = await this.ctxMgr.quests.claim(this.myName, this.token, q.id);
    this.claiming = false;
    if (res) {
      // Pull the new fly balance into the shop wallet so the header + shop agree.
      await this.ctxMgr.shop.refresh();
      this.toast = `+${res.reward} flies claimed!`;
      this.toastT = 2.5;
      this.ctxMgr.audio.play("score");
    } else {
      this.toast = "Couldn't claim that quest.";
      this.toastT = 2.5;
    }
    if (this.entered) this.rebuild();
  }

  private rebuild(): void {
    const buttons: Button[] = [
      backButton(() => this.ctxMgr.goTo(new MenuScene(this.ctxMgr))),
    ];

    if (this.ctxMgr.settings.isLoggedIn()) {
      this.ctxMgr.quests.list().forEach((q, i) => {
        if (!q.complete || q.claimed) return;
        const r = claimRect(LIST_TOP + i * (CARD_H + CARD_GAP));
        buttons.push({ ...r, draw: () => undefined, onPress: () => void this.doClaim(q) });
      });
    }

    this.ctxMgr.buttons.set(buttons);
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);

    drawSceneTitle(ctx, "DAILY QUESTS");
    ctx.font = "11px system-ui, sans-serif";
    ctx.fillText("complete to earn flies — resets daily", VIRTUAL_WIDTH / 2, 60);

    if (!this.ctxMgr.settings.isLoggedIn()) {
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText("Log in to get daily quests.", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT / 2);
      return;
    }

    // Fly balance so reward payouts are visibly reflected.
    ctx.font = "bold 13px system-ui, sans-serif";
    ctx.fillStyle = "#f0d98a";
    ctx.fillText(`${this.ctxMgr.shop.wallet} flies`, VIRTUAL_WIDTH / 2, 86);

    const quests = this.ctxMgr.quests.list();
    if (quests.length === 0) {
      ctx.fillStyle = "#f5e9c8";
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText("Loading today's quests…", VIRTUAL_WIDTH / 2, LIST_TOP + 40);
    }
    quests.forEach((q, i) => this.drawQuestCard(ctx, q, LIST_TOP + i * (CARD_H + CARD_GAP)));

    if (this.toastT > 0) {
      ctx.globalAlpha = Math.min(1, this.toastT);
      ctx.fillStyle = "#9fe6a0";
      ctx.font = "bold 15px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(this.toast, VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT - 40);
      ctx.globalAlpha = 1;
    }
  }

  private drawQuestCard(ctx: CanvasRenderingContext2D, q: QuestView, y: number): void {
    // Card background — claimed quests are dimmed; a claimable one gets a green border.
    const claimable = q.complete && !q.claimed;
    const opts: { fill?: string; border?: string; borderWidth?: number } = {
      borderWidth: claimable ? 3 : 2,
    };
    if (q.claimed) opts.fill = "rgba(243,231,196,0.5)";
    if (claimable) opts.border = "#2e7d32";
    drawCard(ctx, CARD_X, y, CARD_W, CARD_H, 10, opts);

    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#1a1f2b";
    ctx.font = "bold 15px system-ui, sans-serif";
    ctx.fillText(q.label, CARD_X + 14, y + 26);

    // Reward, top-right.
    ctx.textAlign = "right";
    ctx.fillStyle = "#9a6a16";
    ctx.font = "bold 13px system-ui, sans-serif";
    ctx.fillText(`+${q.reward} flies`, CARD_X + CARD_W - 14, y + 26);

    // Progress bar.
    const barX = CARD_X + 14;
    const barY = y + 46;
    const barW = 200;
    const barH = 12;
    ctx.fillStyle = "#d8c79a";
    fillRoundedRect(ctx, barX, barY, barW, barH, 6);
    const frac = q.target > 0 ? Math.min(1, q.progress / q.target) : 0;
    if (frac > 0) {
      ctx.fillStyle = q.complete ? "#2e7d32" : "#7a5e2a";
      fillRoundedRect(ctx, barX, barY, Math.max(barH, barW * frac), barH, 6);
    }

    ctx.textAlign = "left";
    ctx.fillStyle = "#5a4a22";
    ctx.font = "12px system-ui, sans-serif";
    ctx.fillText(`${q.progress} / ${q.target}`, barX, y + 80);

    // Claim button / state, bottom-right.
    const r = claimRect(y);
    if (claimable) {
      drawPillButton(ctx, "Claim", "#2e7d32", r);
    } else if (q.claimed) {
      ctx.textAlign = "right";
      ctx.fillStyle = "#2e7d32";
      ctx.font = "bold 13px system-ui, sans-serif";
      ctx.fillText("Claimed ✓", CARD_X + CARD_W - 14, r.y + 20);
    } else {
      ctx.textAlign = "right";
      ctx.fillStyle = "#8a7a4a";
      ctx.font = "12px system-ui, sans-serif";
      ctx.fillText("in progress", CARD_X + CARD_W - 14, r.y + 20);
    }
  }
}

function drawPillButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  color: string,
  r: { x: number; y: number; w: number; h: number },
): void {
  ctx.fillStyle = color;
  fillRoundedRect(ctx, r.x, r.y, r.w, r.h, 8);
  ctx.fillStyle = "#fff";
  ctx.font = "bold 14px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2);
  ctx.textBaseline = "alphabetic";
}

